import React from 'react';
import { Book, Activity, Wifi, Zap, Database, Clock, Server } from 'lucide-react';

function Documentacion() {
    return (
        <div className="max-w-4xl mx-auto space-y-8 pb-12">
            <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900">Documentación Técnica del Sistema</h2>
                <p className="text-gray-500 mt-2">Especificaciones de funcionamiento, umbrales y lógica de procesamiento</p>
            </div>

            {/* 1. Arquitectura de Datos */}
            <div className="card">
                <div className="flex items-center space-x-3 mb-4">
                    <Database className="w-6 h-6 text-blue-600" />
                    <h3 className="text-xl font-bold text-gray-800">1. Adquisición y Almacenamiento de Datos</h3>
                </div>
                <div className="space-y-4 text-gray-700">
                    <p>
                        El sistema utiliza un esquema de adquisición de datos en tiempo real basado en <strong>Modbus TCP</strong> (para medidores ION), asegurando una lectura precisa y confiable de la energía.
                    </p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li><strong>Frecuencia de Muestreo:</strong> Los datos se leen y almacenan en la base de datos cada <strong>5 segundos</strong>.</li>
                        <li><strong>Base de Datos:</strong> MySQL. Tabla principal: <code>energia</code>.</li>
                        <li><strong>Integridad:</strong> Cada registro incluye un <code>timestamp</code> preciso del momento de la lectura.</li>
                    </ul>
                </div>
            </div>

            {/* 2. Datalink Watchdog */}
            <div className="card">
                <div className="flex items-center space-x-3 mb-4">
                    <Activity className="w-6 h-6 text-emerald-600" />
                    <h3 className="text-xl font-bold text-gray-800">2. Monitor de Enlace (Datalink Watchdog)</h3>
                </div>
                <div className="space-y-4 text-gray-700">
                    <p>
                        El "Watchdog" es un proceso en el servidor que verifica constantemente la salud de la comunicación.
                    </p>
                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                        <h4 className="font-semibold mb-2">Estados del Enlace:</h4>
                        <ul className="space-y-2 text-sm">
                            <li className="flex items-center">
                                <span className="w-3 h-3 rounded-full bg-emerald-500 mr-2"></span>
                                <strong>ONLINE:</strong> Datos recibidos hace menos de 30 segundos.
                            </li>
                            <li className="flex items-center">
                                <span className="w-3 h-3 rounded-full bg-amber-500 mr-2"></span>
                                <strong>ONLINE (0 kW):</strong> Comunicación activa pero potencia de generación nula (Motor detenido o en vacío).
                            </li>
                            <li className="flex items-center">
                                <span className="w-3 h-3 rounded-full bg-red-500 mr-2"></span>
                                <strong>SIN COMUNICACIÓN:</strong> No se han recibido datos nuevos en la base de datos por más de <strong>30 segundos</strong>.
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* 3. Detección de Cortes */}
            <div className="card">
                <div className="flex items-center space-x-3 mb-4">
                    <Zap className="w-6 h-6 text-red-600" />
                    <h3 className="text-xl font-bold text-gray-800">3. Lógica de Detección de Cortes</h3>
                </div>
                <div className="space-y-4 text-gray-700">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="p-4 bg-red-50 rounded border border-red-100">
                            <h4 className="font-bold text-red-800 mb-2 flex items-center">
                                <Wifi className="w-4 h-4 mr-2" /> Corte de Comunicación
                            </h4>
                            <p className="text-sm mb-2">Se considera corte de comunicación cuando existe un "hueco" en el historial de datos.</p>
                            <p className="text-sm font-mono bg-white p-2 rounded border">
                                Diferencia entre registros consecutivas &gt; 30 segundos
                            </p>
                        </div>

                        <div className="p-4 bg-orange-50 rounded border border-orange-100">
                            <h4 className="font-bold text-orange-800 mb-2 flex items-center">
                                <Zap className="w-4 h-4 mr-2" /> Corte de Línea Externa
                            </h4>
                            <p className="text-sm mb-2">Detecta caída de tensión en la red externa. Incluye lógica para evitar falsos positivos si el generador está activo.</p>
                            <p className="text-sm font-mono bg-white p-2 rounded border">
                                Tensión &lt; 5.0 kV  AND  Potencia Generada &lt; 50 kW
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                                * Se requiere Potencia &lt; 50kW para confirmar que no es un error de lectura de tensión mientras el generador está operando.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* 4. Informe Diario */}
            <div className="card">
                <div className="flex items-center space-x-3 mb-4">
                    <FileSpreadsheetIcon className="w-6 h-6 text-purple-600" />
                    <h3 className="text-xl font-bold text-gray-800">4. Generación del Informe Diario</h3>
                </div>
                <div className="space-y-4 text-gray-700">
                    <p>
                        El informe diario procesa los datos brutos minuto a minuto para generar los 96 intervalos estandarizados de CAMMESA.
                    </p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li><strong>Día Operativo:</strong> Inicia a las 00:15 del día seleccionado y finaliza a las 00:00 del día siguiente.</li>
                        <li><strong>Intervalos:</strong> 15 minutos exactos.</li>
                        <li><strong>Cálculo:</strong> Se toma el promedio de potencia y factor de potencia, y la energía acumulada en el intervalo.</li>
                        <li><strong>Estado "Sin Datos":</strong> Si un intervalo de 15 min no tiene suficientes muestras (mínimo 1), se marca como incompleto.</li>
                    </ul>
                </div>
            </div>

            {/* Footer Técnico */}
            <div className="text-center text-xs text-gray-400 pt-8 border-t border-gray-200">
                <p>Sistema desarrollado para CAMMESA Gateway - v2.1.0</p>
                <div className="flex justify-center items-center space-x-2 mt-2">
                    <Server className="w-3 h-3" />
                    <span>Server Timezone: America/Argentina/Buenos_Aires (UTC-3)</span>
                </div>
            </div>
        </div>
    );
}

// Icono auxiliar
function FileSpreadsheetIcon(props) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
            <polyline points="14 2 14 8 20 8" />
            <path d="M8 13h2" />
            <path d="M8 17h2" />
            <path d="M14 13h2" />
            <path d="M14 17h2" />
        </svg>
    );
}

export default Documentacion;
